// See NotificationBar.spec.md for NB-FR-005, NB-FR-006 and NB-FR-007

import { parseTimestamp, sortNewestFirst } from 'core/audit-events/helpers'
import type { AuditEvent } from 'core/audit-events/models'

type Timestamped = Pick<AuditEvent, 'timestamp_utc'>

const MAX_BADGE_COUNT = 99

// NB-FR-006: A test for "strictly newer than the last-seen time", with the last-seen time parsed
// once for a whole list. An event with a missing or unparseable timestamp is never unread, and
// nothing is unread before the last-seen time is known.
export const newerThan = (lastSeenAt: string | null): ((event: Timestamped) => boolean) => {
  const seenTime = parseTimestamp(lastSeenAt)
  if (seenTime === null) return () => false
  return ({ timestamp_utc }) => {
    const eventTime = parseTimestamp(timestamp_utc)
    return eventTime !== null && eventTime > seenTime
  }
}

// NB-FR-006: One event against the last-seen time
export const isNewer = (event: Timestamped, lastSeenAt: string | null): boolean =>
  newerThan(lastSeenAt)(event)

// NB-FR-006: How many events are newer than the last-seen time
export const countNewer = (events: Timestamped[], lastSeenAt: string | null): number => {
  const isUnread = newerThan(lastSeenAt)
  return events.reduce((count, event) => (isUnread(event) ? count + 1 : count), 0)
}

// NB-FR-007: The newest event's own timestamp is saved, never the browser clock
export const newestTimestamp = (events: Timestamped[]): string | null => {
  let newest: string | null = null
  let newestTime = -Infinity
  for (const { timestamp_utc } of events) {
    const time = parseTimestamp(timestamp_utc)
    if (time !== null && time > newestTime) {
      newest = timestamp_utc
      newestTime = time
    }
  }
  return newest
}

// NB-FR-007: The last-seen time never moves backwards
export const laterOf = (a: string | null, b: string | null): string | null => {
  const aTime = parseTimestamp(a)
  const bTime = parseTimestamp(b)
  if (aTime === null) return bTime === null ? null : b
  if (bTime === null) return a
  return bTime > aTime ? b : a
}

// NB-FR-005: Unread events first, then read events, each newest first (one sort, then one pass
// that splits them)
export const sortUnreadFirst = <T extends Timestamped>(events: T[], cutoff: string | null): T[] => {
  const isUnread = newerThan(cutoff)
  const unread: T[] = []
  const read: T[] = []
  for (const event of sortNewestFirst(events)) {
    ;(isUnread(event) ? unread : read).push(event)
  }
  return unread.concat(read)
}

// NB-FR-006: Badge text; empty when there is nothing unread
export const formatUnreadCount = (count: number): string => {
  if (count <= 0) return ''
  return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count)
}

type SearchableRow = Record<'event' | 'object' | 'objectType' | 'initiator', string>

// NB-FR-008: Case-insensitive match on the formatted Event, Object, Object Type and Initiator
export const matchesSearch = (row: SearchableRow, search: string): boolean => {
  const term = search.trim().toLowerCase()
  if (!term) return true
  return [row.event, row.object, row.objectType, row.initiator].some((value) =>
    value.toLowerCase().includes(term),
  )
}
